import { calculateRequiredScope, scopeMatches, withPublicPostReadScopes, withPublicGroupReadScopes, withPublicProfileReadScopes, } from './scopeHelpers.js';
import { viewerBlockedTarget, targetBlockedViewer } from './profileHelpers.js';
export const checkCapabilities = (neededCapabilities, presentCapabilities = {
    add: [],
    remove: [],
}) => {
    const containsCapability = (c, clist) => !!(clist === null || clist === void 0 ? void 0 : clist.find((pc) => pc === c || (pc.includes('*') && c.startsWith(pc.split('*')[0]))));
    const missingCapabilities = neededCapabilities.filter((c) => {
        var _a, _b;
        return containsCapability(c, (_a = presentCapabilities.remove) !== null && _a !== void 0 ? _a : []) ||
            !containsCapability(c, (_b = presentCapabilities.add) !== null && _b !== void 0 ? _b : []);
    });
    return {
        success: missingCapabilities.length === 0,
        missingCapabilities,
    };
};
export const mergeCapabilities = (capabilities) => {
    var _a, _b;
    return {
        add: [...new Set((_a = capabilities === null || capabilities === void 0 ? void 0 : capabilities.map((c) => (c === null || c === void 0 ? void 0 : c.add) || []).flat()) !== null && _a !== void 0 ? _a : [])],
        remove: [
            ...new Set((_b = capabilities === null || capabilities === void 0 ? void 0 : capabilities.map((c) => (c === null || c === void 0 ? void 0 : c.remove) || []).flat()) !== null && _b !== void 0 ? _b : []),
        ],
    };
};
/** Order-independent equality check for two capability add/remove lists. */
export const capabilitySetsEqual = (a, b) => {
    const sa = new Set(a !== null && a !== void 0 ? a : []);
    const sb = new Set(b !== null && b !== void 0 ? b : []);
    if (sa.size !== sb.size)
        return false;
    return [...sa].every((value) => sb.has(value));
};
export const checkRoleCapabilities = (roles = [], neededCapabilities) => checkCapabilities(neededCapabilities, mergeCapabilities(roles.map((r) => r.capabilities)));
/** Any of these matching `roles` shows the Administration row (submenu is separate UX). */
export const ADMIN_SIDEBAR_MENU_CAPABILITIES = [
    'admin',
    'core-roles-read',
    'core-logs-read',
    'core-accounts-list',
    'core-backups-read',
    'core-analytics-read',
    'core-inviteLinks-read',
    'core-maintenance-restart',
    'core-config-read',
    'core-customization-read',
];
export const hasAdminSidebarAccess = (roles = []) => ADMIN_SIDEBAR_MENU_CAPABILITIES.some((cap) => checkRoleCapabilities(roles, [cap]).success);
export const isLocal = (profile) => checkRoleCapabilities(profile.roles, ['core-local']).success;
/**
 * Returns `['local', 'none']` when the caller is "trusted local" — either a local
 * profile or an authenticated service — and `['none']` otherwise.
 */
export const localOrNone = (authData) => {
    if (authData.service) {
        return ['local', 'none'];
    }
    if (authData.profile && isLocal(authData.profile)) {
        return ['local', 'none'];
    }
    return ['none'];
};
export const getGroupRelationships = (authData, group) => {
    var _a, _b, _c;
    const membershipRoles = (_c = (_b = (_a = authData.profile) === null || _a === void 0 ? void 0 : _a.memberships.find((m) => m.group.id === group.id)) === null || _b === void 0 ? void 0 : _b.roles) !== null && _c !== void 0 ? _c : [];
    if (authData.profile || authData.service) {
        return [...localOrNone(authData), ...membershipRoles];
    }
    return ['none'];
};
export const getProfileRelationships = (authData, otherProfile) => {
    const { profile } = authData;
    if (profile && profile.id === (otherProfile === null || otherProfile === void 0 ? void 0 : otherProfile.id)) {
        return ['self', ...localOrNone(authData)];
    }
    const relationships = localOrNone(authData);
    if (profile === null || profile === void 0 ? void 0 : profile.followers.map((f) => f.id).includes(otherProfile === null || otherProfile === void 0 ? void 0 : otherProfile.id)) {
        relationships.push('followed-by');
    }
    if (profile === null || profile === void 0 ? void 0 : profile.following.map((f) => f.id).includes(otherProfile === null || otherProfile === void 0 ? void 0 : otherProfile.id)) {
        relationships.push('following');
    }
    if (viewerBlockedTarget(profile, otherProfile === null || otherProfile === void 0 ? void 0 : otherProfile.id)) {
        relationships.push('blocked');
    }
    if (targetBlockedViewer(profile, otherProfile === null || otherProfile === void 0 ? void 0 : otherProfile.id)) {
        relationships.push('blocked-by');
    }
    return relationships;
};
export const getPostRelationships = (authData, post) => {
    var _a, _b, _c;
    const { profile } = authData;
    const relationships = [];
    if (['public', 'local'].includes(post.visibility)) {
        relationships.push(...localOrNone(authData));
    }
    if (profile && ((_a = post.audience) === null || _a === void 0 ? void 0 : _a.some((a) => a.id === profile.id))) {
        relationships.push('audience');
    }
    if (profile && ((_b = post.mentions) === null || _b === void 0 ? void 0 : _b.some((m) => m.profile.id === profile.id))) {
        relationships.push('mentioned');
    }
    if (profile &&
        ((_c = post.entries) === null || _c === void 0 ? void 0 : _c.some((e) => e.type === 'rsvp' &&
            e.profile.id === profile.id &&
            ['yes', 'tentative'].includes(e.data.response)))) {
        relationships.push('attendee');
    }
    return relationships;
};
export const getReportRelationships = (authData, report) => {
    const { profile } = authData;
    if (profile && report.reporterProfile.id === profile.id) {
        return [...localOrNone(authData), 'reporter'];
    }
    if (profile && report.reportedProfile.id === profile.id) {
        return [...localOrNone(authData), 'reported'];
    }
    return localOrNone(authData);
};
export const getAccessTokenRelationships = (authData, accessToken) => {
    var _a;
    const { profile } = authData;
    const relationships = [...localOrNone(authData)];
    const ownerProfileId = (_a = accessToken.ownedBy) === null || _a === void 0 ? void 0 : _a.id;
    if (ownerProfileId && (profile === null || profile === void 0 ? void 0 : profile.id) === ownerProfileId) {
        relationships.push('owner');
    }
    return relationships;
};
const getConfigCapabilities = (object, relationships, config) => mergeCapabilities(relationships.map((r) => {
    var _a;
    const group = config[object];
    return (_a = group[r]) !== null && _a !== void 0 ? _a : { add: [], remove: [] };
}));
const getGroupRelationshipCapabilities = (authData, group) => mergeCapabilities(group
    ? getGroupRelationships(authData, group).map((r) => { var _a, _b; return (_b = (_a = group.capabilities) === null || _a === void 0 ? void 0 : _a[r]) !== null && _b !== void 0 ? _b : { add: [], remove: [] }; })
    : []);
export const getGroupCapabilitiesByRoles = (roles, group) => mergeCapabilities((roles !== null && roles !== void 0 ? roles : []).map((r) => { var _a, _b; return (_b = (_a = group === null || group === void 0 ? void 0 : group.capabilities) === null || _a === void 0 ? void 0 : _a[r]) !== null && _b !== void 0 ? _b : { add: [], remove: [] }; }));
// Per-group powers (add member, post in group, moderate, …) come from the
// caller's relationship to the group (edge roles plus none/local), never from
// their instance role. Instance roles only gate group *creation*
// (`core-groups-create`), checked separately at the creation entry points.
export const getGroupCapabilities = (authData, group) => getGroupRelationshipCapabilities(authData, group);
const getRoleCapabilities = (profile) => { var _a; return mergeCapabilities((_a = profile === null || profile === void 0 ? void 0 : profile.roles.map((r) => r.capabilities)) !== null && _a !== void 0 ? _a : []); };
export const getProfileCapabilities = (authData, targetProfile, config) => getConfigCapabilities('profile', getProfileRelationships(authData, targetProfile), config);
const getPostVisibilityCapabilities = (post, profile) => {
    switch (post.visibility) {
        case 'public':
            return { add: ['core-posts-read'], remove: [] };
        case 'direct':
            return {};
        // Group posts derive their capabilities from the group relationship
        // (`getGroupCapabilities`) and the author's `self` relationship only —
        // never from instance roles. This keeps per-group powers consistent with
        // every other group check (see getGroupCapabilities); instance roles only
        // gate group creation.
        case 'group':
            return {};
        default:
            return getRoleCapabilities(profile);
    }
};
const getScopedPostReadCapabilities = (authData, neededCapabilities, post) => {
    if (authData.profile ||
        post.visibility !== 'local' ||
        neededCapabilities.length !== 1 ||
        neededCapabilities[0] !== 'core-posts-read' ||
        !scopeMatches({
            scopes: authData.scopes,
            requiredScope: {
                scopeLevel: 'admin',
                resource: { type: 'posts', id: post.id },
            },
        })) {
        return { add: [], remove: [] };
    }
    return { add: ['core-posts-read'], remove: [] };
};
export const getPostCapabilities = (authData, post, config) => mergeCapabilities([
    getPostVisibilityCapabilities(post, authData.profile),
    getConfigCapabilities('post', getPostRelationships(authData, post), config),
    getConfigCapabilities('profile', getProfileRelationships(authData, post.profile), config),
    getGroupCapabilities(authData, post.group),
]);
const getReportCapabilities = (authData, report, config) => mergeCapabilities([
    getConfigCapabilities('report', getReportRelationships(authData, report), config),
    getRoleCapabilities(authData.profile),
]);
const getAccessTokenCapabilities = (authData, accessToken, config) => mergeCapabilities([
    getConfigCapabilities('accessToken', getAccessTokenRelationships(authData, accessToken), config),
    getRoleCapabilities(authData.profile),
]);
const getAccountCapabilities = (authData, targetAccount) => {
    var _a;
    return mergeCapabilities([
        ((_a = authData.account) === null || _a === void 0 ? void 0 : _a.id) === targetAccount.id
            ? { add: ['core-accounts-update'], remove: [] }
            : { add: [], remove: [] },
        getRoleCapabilities(authData.profile),
    ]);
};
const checkCapabilitiesWithCalculatedScope = (capabilities, authData, neededCapabilities, resource) => {
    const requiredScope = calculateRequiredScope({
        resource,
        requiredCapabilities: neededCapabilities,
    });
    return checkCapabilitiesWithExplicitScope(capabilities, authData, neededCapabilities, requiredScope);
};
const checkCapabilitiesWithExplicitScope = (capabilities, authData, neededCapabilities, requiredScope) => {
    const capabilitiesResult = checkCapabilities(neededCapabilities, capabilities);
    const scopeResult = scopeMatches({ scopes: authData.scopes, requiredScope });
    return {
        success: capabilitiesResult.success && scopeResult,
        missingCapabilities: capabilitiesResult.missingCapabilities,
        missingScope: scopeResult ? undefined : requiredScope,
    };
};
export const checkGroupCapabilities = (authData, neededCapabilities, group) => checkCapabilitiesWithCalculatedScope(getGroupCapabilities(authData, group), withPublicGroupReadScopes(authData, group, neededCapabilities), neededCapabilities, { type: 'groups', id: group.id });
export const checkPostCapabilities = (authData, neededCapabilities, post, config) => {
    const capabilities = mergeCapabilities([
        getPostCapabilities(authData, post, config),
        getScopedPostReadCapabilities(authData, neededCapabilities, post),
    ]);
    return checkCapabilitiesWithCalculatedScope(capabilities, withPublicPostReadScopes(authData, post, neededCapabilities), neededCapabilities, { type: 'posts', id: post.id });
};
export const checkProfileCapabilities = (authData, neededCapabilities, targetProfile, config) => checkCapabilitiesWithCalculatedScope(getProfileCapabilities(authData, targetProfile, config), withPublicProfileReadScopes(authData, targetProfile, neededCapabilities, config), neededCapabilities, { type: 'profiles', id: targetProfile.id });
export const checkReportCapabilities = (authData, neededCapabilities, report, config) => checkCapabilitiesWithCalculatedScope(getReportCapabilities(authData, report, config), authData, neededCapabilities, { type: 'reports', id: report.id });
export const checkAccessTokenCapabilities = (authData, neededCapabilities, accessToken, config) => checkCapabilitiesWithExplicitScope(getAccessTokenCapabilities(authData, accessToken, config), authData, neededCapabilities, { scopeLevel: 'admin', resource: { type: 'self' } });
export const checkAccountCapabilities = (authData, neededCapabilities, targetAccount) => checkCapabilitiesWithExplicitScope(getAccountCapabilities(authData, targetAccount), authData, neededCapabilities, { scopeLevel: 'admin', resource: { type: 'self' } });
/** Capability required to create accounts when public sign-ups are closed. */
export const ACCOUNT_CREATE_CAPABILITY = 'core-accounts-create';
/**
 * Whether the caller may register a new account/profile while sign-ups are closed.
 * Profile tokens need the role capability; service tokens are treated as having it
 * and are gated by write scope on profiles (same as other create operations).
 */
export const checkAccountCreateAuthorization = (authData) => {
    const neededCapabilities = [ACCOUNT_CREATE_CAPABILITY];
    const capabilities = authData.service
        ? { add: neededCapabilities, remove: [] }
        : getRoleCapabilities(authData.profile);
    return checkCapabilitiesWithCalculatedScope(capabilities, authData, neededCapabilities, { type: 'profiles', id: '*' });
};
